"use client"

import { useEffect, useState } from "react"
import PaintSplatter from "./paint-splatter"

export default function Header({ profile }) {
  // Define colors directly in the component
  const colors = {
    primary: "#7749F8",
    primaryDark: "#6438E4",
    secondary: "#FFBA07",
    white: "#FFFFFF",
    black: "#000000",
    gray: {
      100: "#f3f4f6",
      200: "#e5e7eb",
      300: "#d1d5db",
      400: "#9ca3af",
      500: "#6b7280",
      600: "#4b5563",
      700: "#374151",
      800: "#1f2937",
      900: "#111827",
    },
  }

  const [isTranscriptModalOpen, setIsTranscriptModalOpen] = useState(false)
  const [currentTranscriptIndex, setCurrentTranscriptIndex] = useState(0)

  const toggleTranscriptModal = () => {
    setIsTranscriptModalOpen(!isTranscriptModalOpen)
  }

  const nextTranscript = () => {
    setCurrentTranscriptIndex((prevIndex) => (prevIndex + 1) % profile.transcripts.length)
  }

  const prevTranscript = () => {
    setCurrentTranscriptIndex((prevIndex) => (prevIndex - 1 + profile.transcripts.length) % profile.transcripts.length)
  }

  const containerStyle = {
    position: "relative",
    backgroundColor: colors.white,
    margin: "1.5rem 0 0 0",
    overflow: "hidden",
  }

  const containerInnerStyle = {
    maxWidth: "1200px",
    margin: "0 auto",
    padding: "0 1rem",
    position: "relative",
    zIndex: 10,
  }

  const flexContainerStyle = {
    display: "flex",
    flexWrap: "wrap",
    margin: "0 -1rem",
  }

  const columnStyle = {
    width: "100%",
    padding: "0 1rem",
  }

  const primaryTextStyle = {
    color: colors.primary,
    marginBottom: "0.75rem",
    animation: "fadeIn 1s ease-out forwards",
  }

  const headingStyle = {
    fontSize: "3rem",
    marginBottom: "0.75rem",
    fontWeight: "bold",
    animation: "fadeIn 1s ease-out forwards",
  }

  const buttonContainerStyle = {
    display: "flex",
    alignItems: "center",
    paddingTop: "1.25rem",
  }

  const primaryButtonStyle = {
    backgroundColor: colors.primary,
    color: colors.white,
    padding: "0.75rem 1rem",
    marginRight: "1.25rem",
    borderRadius: "0.25rem",
    transition: "background-color 0.3s",
    textDecoration: "none",
    cursor: "pointer",
  }

  const playIconStyle = {
    position: "absolute",
    width: 0,
    height: 0,
    borderTop: "0.5rem solid transparent",
    borderBottom: "0.5rem solid transparent",
    borderLeft: `0.75rem solid ${colors.primary}`,
    marginLeft: "0.25rem",
  }

  const playTextStyle = {
    marginLeft: "1rem",
    marginBottom: 0,
    display: "none",
  }

  // Media query styles to be applied with JavaScript
  useEffect(() => {
    const handleResize = () => {
      const isLargeScreen = window.innerWidth >= 1024

      // Update column styles based on screen size
      const leftColumn = document.getElementById("header-left-column")
      const rightColumn = document.getElementById("header-right-column")
      const playText = document.getElementById("play-text")

      if (leftColumn && rightColumn) {
        if (isLargeScreen) {
          leftColumn.style.width = "50%"
          rightColumn.style.width = "50%"
          if (playText) playText.style.display = "block"
        } else {
          leftColumn.style.width = "100%"
          rightColumn.style.width = "100%"
          if (playText) playText.style.display = "none"
        }
      }
    }

    // Initial call and event listener
    handleResize()
    window.addEventListener("resize", handleResize)

    return () => {
      window.removeEventListener("resize", handleResize)
    }
  }, [])

  return (
    <div style={containerStyle} id="home">
      <PaintSplatter position="top-left" size="xl" opacity={0.15} rotation={-15} />
      <PaintSplatter position="top-right" size="lg" opacity={0.2} rotation={25} />
      <div style={containerInnerStyle}>
        <div style={flexContainerStyle}>
          <div id="header-left-column" style={columnStyle}>
            <h3 style={primaryTextStyle}>I'm</h3>
            <h1 style={headingStyle}>{profile?.name || "Loading..."}</h1>
            <div style={buttonContainerStyle}>
              <button style={primaryButtonStyle} onClick={toggleTranscriptModal}>
                View certificate
              </button>
            </div>
          </div>
          <div id="header-right-column" style={columnStyle}>
            <img
              style={{
                width: "30%",
                position: "relative",
                zIndex: 10,
                animation: "fadeIn 1s ease-out forwards",
                marginLeft:"18rem",
                borderRadius: "20%",
                boxShadow: `0 4px 8px rgba(${colors.gray[500]}, 0.2)`,
                border: `1px solid ${colors.gray[500]}`,

              }}
              src={profile?.personal_image || "default-image.png"}
              alt="Profile"
            />
          </div>
        </div>
      </div>

      {/* Modal for transcripts */}
      {isTranscriptModalOpen && profile?.transcripts?.length > 0 && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 1000,
          }}
        >
          <div
            style={{
              backgroundColor: "white",
              borderRadius: "0.5rem",
              boxShadow: "0 4px 8px rgba(0, 0, 0, 0.2)",
              padding: "2rem",
              width: "90%",
              maxWidth: "500px",
              textAlign: "center",
            }}
          >
            <img
              src={profile.transcripts[currentTranscriptIndex]}
              alt={`Transcript ${currentTranscriptIndex + 1}`}
              style={{
                width: "100%",
                height: "auto",
                marginBottom: "1rem",
                borderRadius: "0.25rem",
              }}
            />
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "1rem" }}>
              <button
                style={{
                  backgroundColor: "#e5e7eb",
                  color: "#1f2937",
                  fontWeight: "bold",
                  padding: "0.5rem 1rem",
                  borderRadius: "0.25rem",
                  cursor: "pointer",
                }}
                onClick={prevTranscript}
              >
                Previous
              </button>
              <button
                style={{
                  backgroundColor: "#e5e7eb",
                  color: "#1f2937",
                  fontWeight: "bold",
                  padding: "0.5rem 1rem",
                  borderRadius: "0.25rem",
                  cursor: "pointer",
                }}
                onClick={nextTranscript}
              >
                Next
              </button>
            </div>
            <button
              style={{
                backgroundColor: "#f87171",
                color: "white",
                fontWeight: "bold",
                padding: "0.75rem 1rem",
                borderRadius: "0.25rem",
                cursor: "pointer",
                width: "100%",
              }}
              onClick={toggleTranscriptModal}
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

